"use client";

import { useTranslations } from "next-intl";
import { motion } from "motion/react";
import { GradientText, RevealOnScroll } from "./_primitives";

export default function FinalCtaSection() {
    const t = useTranslations();

    const scrollToForm = () => {
        const el = document.getElementById("instructor-application");
        if (!el) return;
        el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const steps = [
        {
            num: "01",
            tag: t("joinInstructor_process_step1_tag"),
            title: t("joinInstructor_process_step1_title"),
            desc: t("joinInstructor_process_step1_desc"),
        },
        {
            num: "02",
            tag: t("joinInstructor_process_step2_tag"),
            title: t("joinInstructor_process_step2_title"),
            desc: t("joinInstructor_process_step2_desc"),
        },
        {
            num: "03",
            tag: t("joinInstructor_process_step3_tag"),
            title: t("joinInstructor_process_step3_title"),
            desc: t("joinInstructor_process_step3_desc"),
        },
    ];

    return (
        <section className="relative py-20 sm:py-24 lg:py-32 overflow-hidden">
            {/* ambient glows */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute -bottom-40 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-[radial-gradient(circle,rgba(232,81,29,0.22),transparent_60%)] blur-3xl" />
                <div className="absolute -top-40 right-0 w-[420px] h-[420px] rounded-full bg-[radial-gradient(circle,rgba(229,21,120,0.18),transparent_60%)] blur-3xl" />
            </div>

            <div className="relative max-w-3xl mx-auto px-5 sm:px-6 lg:px-10 text-center">
                <RevealOnScroll>
                    <h2
                        className="font-bold text-gray-900 dark:text-white leading-[1] tracking-tight"
                        style={{
                            fontFamily: "'Playfair Display', Georgia, serif",
                            fontSize: "clamp(2rem, 5vw, 4rem)",
                        }}
                    >
                        {t("joinInstructor_final_title_part1")}
                        <br />
                        <GradientText>
                            {t("joinInstructor_final_title_emphasis")}
                        </GradientText>
                        <span className="text-primary"> ?</span>
                    </h2>
                </RevealOnScroll>

                <RevealOnScroll delay={0.05}>
                    <p
                        className="mt-5 sm:mt-6 text-lg sm:text-xl text-gray-700 dark:text-white/80"
                        style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                    >
                        {t("joinInstructor_final_subtitle_part1")}{" "}
                        <span className="text-primary font-semibold">
                            {t("joinInstructor_final_subtitle_emphasis")}
                        </span>
                    </p>
                </RevealOnScroll>

                <RevealOnScroll delay={0.15}>
                    <motion.button
                        onClick={scrollToForm}
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                        className="mt-9 sm:mt-10 group relative inline-flex items-center gap-3 px-7 sm:px-8 py-3.5 sm:py-4 rounded-xl bg-gradient-to-r from-[#FF7A1A] via-[#FF3B5C] to-[#E51578] text-white font-semibold text-sm sm:text-base shadow-[0_12px_40px_-8px_rgba(232,81,29,0.55)] hover:shadow-[0_16px_60px_-8px_rgba(232,81,29,0.75)] transition-shadow"
                    >
                        <span>{t("joinInstructor_final_cta")}</span>
                        <svg
                            className="w-4 sm:w-5 h-4 sm:h-5 transition-transform group-hover:translate-x-1"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                        >
                            <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" />
                        </svg>
                    </motion.button>
                </RevealOnScroll>
            </div>

            {/* 3-step horizontal timeline */}
            <div className="relative max-w-5xl mx-auto px-5 sm:px-6 lg:px-10 mt-20 sm:mt-24">
                <RevealOnScroll>
                    <div className="grid sm:grid-cols-3 gap-10 sm:gap-6 lg:gap-10 relative">
                        {/* connecting line */}
                        <div
                            aria-hidden="true"
                            className="hidden sm:block absolute top-3 left-[16%] right-[16%] h-px bg-gradient-to-r from-primary/30 via-primary/50 to-primary/30"
                        />
                        {steps.map((step, i) => (
                            <motion.div
                                key={step.num}
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.3 }}
                                transition={{
                                    duration: 0.6,
                                    delay: 0.15 + i * 0.15,
                                    ease: [0.16, 1, 0.3, 1],
                                }}
                                className="relative text-center sm:px-3"
                            >
                                <span className="relative z-10 inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary text-white text-[11px] font-mono font-bold shadow-[0_0_18px_rgba(232,81,29,0.5)]">
                                    {step.num}
                                </span>
                                <div className="mt-5">
                                    <span className="text-[10px] uppercase tracking-[0.25em] text-gray-400 dark:text-white/40 font-mono">
                                        {step.tag}
                                    </span>
                                    <h3
                                        className="mt-2 text-lg sm:text-xl text-gray-900 dark:text-white font-bold"
                                        style={{
                                            fontFamily:
                                                "'Playfair Display', Georgia, serif",
                                        }}
                                    >
                                        {step.title}
                                    </h3>
                                    <p className="mt-3 text-sm text-gray-600 dark:text-white/70 leading-relaxed max-w-xs mx-auto">
                                        {step.desc}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </RevealOnScroll>
            </div>

        </section>
    );
}
